import React, { useState } from 'react';
import { Field } from 'formik';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import IdntyEmailStep from './IdntyEmailStep';
import IdntyGoogleOption from './IdntyGoogleOption';
import Text from '../../../../shared/_TBComponents/Text';

const InfoText = styled('p')`
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 15px;
  text-align: center;
`;

const FormGroup = styled('div')`
  margin-bottom: 5px;
  z-index: 1;

  label {
    width: 100%;
  }
`;

const ErrorAndName = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 5px;
  width: 100%;
`;

const FieldError = styled('div')`
  color: #ff6600;
  font-size: 12px;
`;

const PasswordFieldWrapper = styled('div')`
  position: relative;
`;

const PasswordToggleButton = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 8px;
  pointer-events: auto;
  position: absolute !important;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:hover {
    color: #333;
  }
`;

const PasswordToggleIcon = styled('span')`
  margin-left: 8px;
`;

// hasPassword alone can't rule out a Google-only account (the database
// always creates one independently), so Google sign-in is offered here too,
// not just on the front step. A non-matching Google account still falls
// through to NEW_USER, same as the front step.
const IdntyExistingUserStep = ({
  cameFromDuplicateEmail,
  errors,
  onGoogleError,
  onGoogleSuccess,
  touched,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  return (
    <>
      <InfoText>
        {cameFromDuplicateEmail
          ? 'An account with this email already exists. Sign in with Google or enter your password to link it to IDNTY.'
          : 'Welcome back! Sign in with Google or enter your password to join IDNTY.'}
      </InfoText>
      <IdntyGoogleOption
        onGoogleError={onGoogleError}
        onGoogleSuccess={onGoogleSuccess}
      />
      <IdntyEmailStep disabled error={errors.emailAddress} touched={touched.emailAddress} />
      <FormGroup>
        <label htmlFor='password'>
          <ErrorAndName>
            <Text
              styledTextProps={{ fontSize: theme.textFontSizes[0] }}
            >
              Password
            </Text>
            {errors.password && touched.password && <FieldError className='text-center'>{errors.password}</FieldError>}
          </ErrorAndName>
          <PasswordFieldWrapper>
            <Field
              autoComplete='off'
              className='form-control'
              id='password'
              name='password'
              placeholder='Password'
              type={showPassword ? 'text' : 'password'}
            />
            <PasswordToggleButton
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={-1}
              type='button'
            >
              {showPassword ? 'Hide Password' : 'Show Password'}
              <PasswordToggleIcon>
                {showPassword ? <HiEyeOff /> : <HiEye />}
              </PasswordToggleIcon>
            </PasswordToggleButton>
          </PasswordFieldWrapper>
        </label>
      </FormGroup>
    </>
  );
};

IdntyExistingUserStep.propTypes = {
  cameFromDuplicateEmail: PropTypes.bool,
  errors: PropTypes.shape({
    emailAddress: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
  onGoogleError: PropTypes.func.isRequired,
  onGoogleSuccess: PropTypes.func.isRequired,
  touched: PropTypes.shape({
    emailAddress: PropTypes.bool,
    password: PropTypes.bool,
  }).isRequired,
};

IdntyExistingUserStep.defaultProps = {
  cameFromDuplicateEmail: false,
};

export default IdntyExistingUserStep;
